import { useMemo } from "react";
import { useShallow } from "zustand/react/shallow";

import { Stack, Package, Policy, Puzzle } from "components/icons/generated";

import useCommandPaletteStore from "../store/useCommandPaletteStore";
import { detectSearchIntent } from "../utils/detectSearchIntent";
import { CommandPaletteActionType, CommandPaletteBucket } from "../types";
import { BUCKET_IDS, SUB_LIST_VIEWS } from "../constants";

const useCommandPaletteSearchBucket = (): CommandPaletteBucket => {
  const { searchQuery, setSubList } = useCommandPaletteStore(
    useShallow((state) => ({
      searchQuery: state.searchQuery,
      setSubList: state.setSubList,
    }))
  );

  return useMemo(
    () => ({
      id: BUCKET_IDS.SEARCH,
      title: "Search",
      order: 999,
      getActions: () => [
        {
          type: CommandPaletteActionType.SUB_LIST_OPENER,
          id: "search-stacks",
          title: "Search stacks",
          alwaysVisible: true,
          bucketId: BUCKET_IDS.SEARCH,
          icon: Stack,
          onAction: () => {
            const intent = detectSearchIntent({ title: "Search stacks" }, searchQuery);
            const initialQuery = intent ? searchQuery : "";
            setSubList(SUB_LIST_VIEWS.STACKS_SEARCH, ["Search stacks"], initialQuery);
          },
        },
        {
          type: CommandPaletteActionType.SUB_LIST_OPENER,
          id: "search-modules",
          title: "Search modules",
          alwaysVisible: true,
          bucketId: BUCKET_IDS.SEARCH,
          icon: Package,
          onAction: () => {
            const intent = detectSearchIntent({ title: "Search modules" }, searchQuery);
            const initialQuery = intent ? searchQuery : "";
            setSubList(SUB_LIST_VIEWS.MODULES_SEARCH, ["Search modules"], initialQuery);
          },
        },
        {
          type: CommandPaletteActionType.SUB_LIST_OPENER,
          id: "search-policies",
          title: "Search policies",
          alwaysVisible: true,
          bucketId: BUCKET_IDS.SEARCH,
          icon: Policy,
          onAction: () => {
            const intent = detectSearchIntent({ title: "Search policies" }, searchQuery);
            const initialQuery = intent ? searchQuery : "";
            setSubList(SUB_LIST_VIEWS.POLICIES_SEARCH, ["Search policies"], initialQuery);
          },
        },
        {
          type: CommandPaletteActionType.SUB_LIST_OPENER,
          id: "search-contexts",
          title: "Search contexts",
          alwaysVisible: true,
          bucketId: BUCKET_IDS.SEARCH,
          icon: Puzzle,
          onAction: () => {
            const intent = detectSearchIntent({ title: "Search contexts" }, searchQuery);
            const initialQuery = intent ? searchQuery : "";
            setSubList(SUB_LIST_VIEWS.CONTEXTS_SEARCH, ["Search contexts"], initialQuery);
          },
        },
      ],
    }),
    [searchQuery, setSubList]
  );
};

export default useCommandPaletteSearchBucket;
